const PLACEHOLDER_IMAGE_HOSTNAMES = new Set([
  'example.com',
  'www.example.com',
  'cdn.example.com'
])

const ANIMATED_GIF_PATH_RE = /\.gif$/i
export function shouldUnoptimizeImageSrc(src?: string | null): boolean {
  const trimmedValue = src?.trim()
  if (!trimmedValue || trimmedValue.startsWith('/')) {
    return false
  }

  try {
    const pathname = new URL(trimmedValue).pathname
    return ANIMATED_GIF_PATH_RE.test(pathname)
  } catch {
    return ANIMATED_GIF_PATH_RE.test(trimmedValue.split(/[?#]/)[0] ?? '')
  }
}

const SVG_PATH_RE = /\.svg$/i
export function isSvgImageSrc(src?: string | null): boolean {
  const trimmedValue = src?.trim()
  if (!trimmedValue) {
    return false
  }

  return SVG_PATH_RE.test(trimmedValue.split(/[?#]/)[0] ?? '')
}

export function withImageCacheBust(url?: string | null): string {
  const trimmed = url?.trim()
  if (!trimmed) {
    return ''
  }

  try {
    const parsed = new URL(trimmed)
    parsed.searchParams.set('v', Date.now().toString())
    return parsed.toString()
  } catch {
    return trimmed
  }
}

const PRESIGNED_URL_RE = /[?&]x-amz-signature=/i
const stripTrailingSlashes = (value?: string) =>
  value?.trim().replace(/\/+$/, '') ?? ''

/** Display-only: S3 origin → CDN origin. Never use for presigned uploads or form payloads. */
export function toMediaCdnUrl<T extends string | null | undefined>(url: T): T {
  const s3Origin = stripTrailingSlashes(process.env.NEXT_PUBLIC_MEDIA_S3_URL)
  const cdnOrigin = stripTrailingSlashes(process.env.NEXT_PUBLIC_MEDIA_CDN_URL)
  if (!url || !s3Origin || !cdnOrigin || !url.startsWith(s3Origin)) {
    return url
  }

  const rest = url.slice(s3Origin.length)
  if ((rest && !/^[/?#]/.test(rest)) || PRESIGNED_URL_RE.test(rest)) {
    return url
  }
  return `${cdnOrigin}${rest}` as T
}

export function sanitizeExternalImageUrl(
  value?: string | null
): string | undefined {
  const trimmedValue = value?.trim()
  if (!trimmedValue) return undefined
  if (trimmedValue.startsWith('/')) return trimmedValue

  try {
    const parsedUrl = new URL(trimmedValue)
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return undefined
    }
    if (PLACEHOLDER_IMAGE_HOSTNAMES.has(parsedUrl.hostname)) {
      return undefined
    }
    return toMediaCdnUrl(trimmedValue)
  } catch {
    return undefined
  }
}
